// ─────────────────────────────────────────────────────────────────────────────
// IgnoreCodeModalComponent
// ─────────────────────────────────────────────────────────────────────────────
// Dialog for creating and editing ignore-list entries (source='manual' only).
// Customer is optional — leaving it empty creates a global ignore entry that
// filters the code regardless of which customer placed the order.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { AppState } from 'src/app/core/app.state';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedDialogComponent } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';

import { ICreateIgnoreCodePayload, IIgnoreCode, IUpdateIgnoreCodePayload } from '../../models/ignore-code.models';
import { CreateIgnoreCode, CreateIgnoreCodeSuccess, UpdateIgnoreCode, UpdateIgnoreCodeSuccess } from '../../store/ignore-codes.actions';
import { selectIgnoreCodeCreating, selectIgnoreCodeUpdating } from '../../store/ignore-codes.selectors';

interface SelectOption {
  value: number;
  label: string;
}

@Component({
  selector: 'app-ignore-code-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, SharedModule, UiModule, SharedDialogComponent],
  templateUrl: './ignore-code-modal.component.html',
  styleUrls: ['./ignore-code-modal.component.scss'],
})
export class IgnoreCodeModalComponent implements OnInit, OnDestroy {
  // ── Form ──────────────────────────────────────────────────────────────────

  form!: FormGroup;
  customerSearchControl = new FormControl('');

  // ── State ─────────────────────────────────────────────────────────────────

  isUpdate = false;
  canSubmit = false;
  canSubmit$: Observable<boolean> = of(false);
  submitting$: Observable<boolean> = of(false);

  // ── Autocomplete state ────────────────────────────────────────────────────

  filteredCustomers$!: Observable<SelectOption[]>;
  customerSearchState: 'idle' | 'searching' | 'results' = 'idle';

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<IgnoreCodeModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IIgnoreCode | null,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private actions$: Actions,
    private translateService: TranslateService,
    private customersService: CustomersService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.initForm();
    this.initCustomerAutocomplete();
    this.initClearDetection();
    this.handleEditMode();

    this.canSubmit$ = this.store.select(selectCanDo('knowledge_base', this.isUpdate ? 'edit' : 'create'));
    this.canSubmit$.pipe(takeUntil(this.destroy$)).subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
      if (canSubmit) {
        this.form.enable({ emitEvent: false });
        this.customerSearchControl.enable({ emitEvent: false });
      } else {
        this.form.disable({ emitEvent: false });
        this.customerSearchControl.disable({ emitEvent: false });
      }
    });

    this.submitting$ = combineLatest([this.store.select(selectIgnoreCodeCreating), this.store.select(selectIgnoreCodeUpdating)]).pipe(
      map(([creating, updating]) => creating || updating),
    );

    this.listenForSuccess();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Initialization ────────────────────────────────────────────────────────

  private initForm(): void {
    this.form = this.fb.group({
      code: ['', [Validators.required, Validators.maxLength(255)]],
      customer_id: [null],
    });
  }

  private initCustomerAutocomplete(): void {
    this.filteredCustomers$ = this.customerSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const keyword = typeof term === 'string' ? term : '';
        if (!keyword || keyword.length < 2) {
          this.customerSearchState = 'idle';
          return of([] as SelectOption[]);
        }
        this.customerSearchState = 'searching';
        return this.customersService.listCustomers({ number: 1, size: 20 }, undefined, keyword).pipe(
          map((res) => {
            const options: SelectOption[] = (res.data ?? []).map((c) => ({
              value: c.id,
              label: c.name,
            }));
            this.customerSearchState = 'results';
            return options;
          }),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  private initClearDetection(): void {
    this.customerSearchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (typeof value === 'string' && !value) {
        this.form.patchValue({ customer_id: null });
      }
    });
  }

  private handleEditMode(): void {
    if (!this.data) return;

    this.isUpdate = true;

    this.form.patchValue({
      code: this.data.code,
      customer_id: this.data.customer_id,
    });

    if (this.data.customer_id && this.data.customer_name) {
      this.customerSearchControl.setValue({ value: this.data.customer_id, label: this.data.customer_name } as unknown as string);
    }
  }

  private listenForSuccess(): void {
    this.actions$.pipe(ofType(CreateIgnoreCodeSuccess, UpdateIgnoreCodeSuccess), take(1), takeUntil(this.destroy$)).subscribe(() => this.close());
  }

  // ── Autocomplete handlers ─────────────────────────────────────────────────

  onCustomerSelected(option: SelectOption): void {
    this.form.patchValue({ customer_id: option.value });
  }

  clearCustomer(): void {
    this.customerSearchControl.setValue('');
    this.form.patchValue({ customer_id: null });
  }

  displayCustomerFn = (option: SelectOption | string): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label ?? '';
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  submitPermission() {
    return { module: 'knowledge_base', action: this.isUpdate ? 'edit' : 'create' };
  }

  save(): void {
    if (!this.canSubmit) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting$.pipe(take(1), takeUntil(this.destroy$)).subscribe((isSubmitting) => {
      if (isSubmitting) return;

      const raw = this.form.getRawValue();
      const code = (raw.code ?? '').trim();
      const customerId = raw.customer_id ?? null;

      if (this.isUpdate && this.data) {
        const payload: IUpdateIgnoreCodePayload = { code };
        payload.customer_id = customerId;
        this.store.dispatch(UpdateIgnoreCode({ id: this.data.id, payload }));
      } else {
        const payload: ICreateIgnoreCodePayload = { code };
        if (customerId != null) {
          payload.customer_id = customerId;
        }
        this.store.dispatch(CreateIgnoreCode({ payload }));
      }
    });
  }

  // ── Close ─────────────────────────────────────────────────────────────────

  close(): void {
    this.dialogRef.close();
  }
}
